// Phase 35c — SSE client for the Copilot streaming endpoint.
//
// We can't use axios for SSE (it buffers the full response). Native `fetch`
// + a ReadableStream reader is the standard approach and works in every
// evergreen browser without polyfills.
//
// Wire contract (one JSON object per `data:` frame):
//   {"phase": "thinking"}
//   {"tool_call_start": {"iteration": 1, "name": "...", "args": {...}}}
//   {"tool_call_result": {"iteration": 1, "name": "...", "result": {...}}}
//   {"token": "next words "}
//   {"done": true, "metadata": {...}, "conversation_id": "..."}
//
// The endpoint sends frames in the form `data: <json>\n\n` (eventstream
// format) — our parser splits on blank lines and strips the `data: ` prefix.

import { API_BASE } from "@/lib/api";

/**
 * Stream a Copilot reply. Returns a Promise that resolves once the `done`
 * frame is observed, OR rejects on transport error / HTTP error / abort.
 *
 * @param {Object}   opts
 * @param {string}   opts.conversationId
 * @param {string}   opts.content                  - the user's message
 * @param {Function} opts.onPhase                  - (phase) => void
 * @param {Function} opts.onToolCallStart          - ({iteration,name,args}) => void
 * @param {Function} opts.onToolCallResult         - ({iteration,name,result}) => void
 * @param {Function} opts.onToken                  - (chunk) => void
 * @param {Function} opts.onDone                   - (metadata) => void
 * @param {AbortSignal} [opts.signal]              - cancel mid-stream
 */
export async function streamCopilot({
  conversationId,
  content,
  onPhase,
  onToolCallStart,
  onToolCallResult,
  onToken,
  onDone,
  signal,
}) {
  const url = `${API_BASE}/copilot/conversations/${conversationId}/messages/stream`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
    signal,
  });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json())?.detail || ""; } catch { /* ignore */ }
    throw new Error(`Copilot stream HTTP ${res.status}${detail ? `: ${detail}` : ""}`);
  }
  if (!res.body) {
    throw new Error("Copilot stream: no response body (browser unsupported?)");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are delimited by a blank line. Use a regex split tolerant
    // to both \n\n and \r\n\r\n.
    const chunks = buffer.split(/\n\n|\r\n\r\n/);
    // Last partial chunk goes back into the buffer.
    buffer = chunks.pop() ?? "";

    for (const raw of chunks) {
      const line = raw.trim();
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      let frame;
      try {
        frame = JSON.parse(payload);
      } catch {
        // Corrupt frame — skip.
        continue;
      }
      if (frame.phase) onPhase?.(frame.phase);
      else if (frame.tool_call_start) onToolCallStart?.(frame.tool_call_start);
      else if (frame.tool_call_result) onToolCallResult?.(frame.tool_call_result);
      else if (frame.token !== undefined) onToken?.(frame.token);
      else if (frame.done) { onDone?.(frame.metadata || {}); return; }
    }
  }
  // Stream closed before a `done` frame. Treat as soft-end.
  onDone?.({ ai_status: "stream_truncated" });
}
